import React from "react";
import { Badge, Card, Col, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next"; // Import useTranslation

import ExpenseList from "../ExpenseList";
import PieChart from "../StatsChart/PieChart";
import { FaCalendarAlt } from "react-icons/fa";
import LineGraph from "../StatsChart/LineGraph";
import StackedBarChart from "../StatsChart/StackedBarChart";
import CalendarHeatmap from "../StatsChart/CalendarHeatmap";

const MonthView = ({ date, expensesItems, setViewMode, setCurrentDate }) => {
  const { t } = useTranslation(); // Initialize useTranslation hook
  const year = date.getFullYear();
  const month = date.getMonth();

  const getDaysInMonth = (year, month) => {
    return new Date(year, month + 1, 0).getDate();
  };

  const daysInMonth = getDaysInMonth(year, month);
  const daysArray = Array.from({ length: daysInMonth }, (_, i) => new Date(year, month, i + 1));

  let monthExpenseItems = [];
  const totalMonthAmount = daysArray.reduce((tol, day) => {
    const formattedDate = day.toISOString().split("T")[0];
    const itemsForDay = expensesItems.filter((item) => item.purchasedDate === formattedDate);
    monthExpenseItems = [...monthExpenseItems, ...itemsForDay];
    const totalAmount = itemsForDay.reduce((total, item) => total + Number(item.total || 0), 0);
    return tol + totalAmount;
  }, 0);

  const startDate = new Date(year, month, 1);
  const endDate = new Date(year, month + 1, 0);
  const intervals = [];
  let currentDate = new Date(startDate);

  while (currentDate <= endDate) {
    const intervalStart = new Date(currentDate);
    var intervalEnd = new Date(currentDate);
    intervalEnd.setDate(intervalEnd.getDate() + 2);

    if (intervalEnd > endDate) intervalEnd = new Date(endDate);

    intervals.push({ start: intervalStart, end: intervalEnd });
    currentDate = new Date(intervalEnd);
    currentDate.setDate(currentDate.getDate() + 1);
  }

  return (
    <div className="p-3">
      <div className="d-flex align-items-center gap-3 mb-4">
        <FaCalendarAlt size={28} className="text-primary" />
        <div>
          <h3 className="mb-0">
            {date.toLocaleString("default", { month: "long" })} {year}
          </h3>
          <Badge bg="light" text="dark">
            {t("statistics.monthViewComp.monthlyOverview")}
          </Badge>
        </div>
      </div>

      <div className="d-flex justify-content-between align-items-center mb-4 p-3 bg-light rounded-3">
        <div>
          <h5 className="mb-0">{t("statistics.monthViewComp.total", { totalAmount: totalMonthAmount.toFixed(2) })}</h5>
          <small className="text-muted">
            {t("statistics.monthViewComp.averageDaily", { averageAmount: (totalMonthAmount / daysInMonth).toFixed(2) })}
          </small>
        </div>
        <h2 className="mb-0 text-primary">${totalMonthAmount.toFixed(2)}</h2>
      </div>

      <Row className="g-3">
        <Card className="mb-4 shadow-sm">
          <Card.Body>
            <CalendarHeatmap
              date={date}
              expensesItems={expensesItems}
              setViewMode={setViewMode}
              setCurrentDate={setCurrentDate}
            />
          </Card.Body>
        </Card>
      </Row>
      <Row className="mt-4 g-4">
        <Col lg={4}>
          <Card className="h-100 shadow-sm">
            <Card.Body>
              <h5 className="mb-3">{t("statistics.monthViewComp.categoryBreakdown")}</h5>
              <PieChart items={monthExpenseItems} />
            </Card.Body>
          </Card>
        </Col>
        <Col lg={4}>
          <Card className="h-100 shadow-sm">
            <Card.Body>
              <h5 className="mb-3">{t("statistics.monthViewComp.spendingTrends")}</h5>
              <LineGraph items={monthExpenseItems} intervals={intervals} />
            </Card.Body>
          </Card>
        </Col>
        <Col lg={4}>
          <Card className="h-100 shadow-sm">
            <Card.Body>
              <h5 className="mb-3">{t("statistics.monthViewComp.stackedOverview")}</h5>
              <StackedBarChart items={monthExpenseItems} intervals={intervals} />
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Row className="mt-4">
        <Col>
          <Card className="h-100 shadow-sm">
            <Card.Body>
              <h5 className="mb-3">{t("statistics.monthViewComp.expenseDetails")}</h5>
              <ExpenseList items={monthExpenseItems} defaultOpen={false} />
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default MonthView;
